import React, { memo, useState, useRef, useMemo } from 'react';
import MessageInput from './MessageInput';
import FileUploadButton from './FileUploadButton';
import SendButton from './SendButton';
import { MAX_FILE_SIZE } from './UploadedFiles';

/**
 * Input area component
 * Combines message input, file upload, and send functionality
 */
const InputArea = memo(({ 
  input,
  setInput,
  handleChatSubmit,
  fileInputRef,
  handleFileUpload,
  agentStatus,
  isEventConnected,
  isSandboxReady,
  uploadedFiles,
  setUploadedFiles,
  handleStopGeneration,
  removeFile,
  availableHeight,
  readOnly = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // Check if any uploaded files exceed size limit
  const hasOversizedFiles = useMemo(() => {
    return uploadedFiles.some(file => file.size > MAX_FILE_SIZE);
  }, [uploadedFiles]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const event = {
        target: {
          files: e.dataTransfer.files
        }
      };
      handleFileUpload(event);
    }
  };

  return (
    <div 
      className={`relative h-full flex flex-col transition-all duration-200 max-h-[40vh] ${
        isDragOver ? 'bg-brand-primary/5 dark:bg-brand-primary/10 rounded-lg' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 border-2 border-dashed border-brand-primary rounded-lg bg-brand-primary/10 dark:bg-brand-primary/20 flex items-center justify-center z-10 pointer-events-none">
          <div className="text-brand-primary font-medium">Drop files here to upload</div>
        </div>
      )}
      <form onSubmit={handleChatSubmit} className="flex-1 flex items-stretch landscape:h-auto">
        <div className={`flex-1 bg-white dark:bg-neutral-800 border border-slate-300 dark:border-neutral-700 rounded-lg overflow-hidden flex items-stretch ${
          isDragOver ? 'border-brand-primary border-2' : ''
        }`}>
            <MessageInput
              input={input}
              setInput={setInput}
              isFocused={isFocused}
              setIsFocused={setIsFocused}
              isEventConnected={isEventConnected}
              agentStatus={agentStatus}
              isSandboxReady={isSandboxReady}
              uploadedFiles={uploadedFiles}
              handleChatSubmit={handleChatSubmit}
              availableHeight={availableHeight}
              readOnly={readOnly}
            />
            
            <div className="flex items-center pr-1 sm:pr-2 pb-2 sm:pb-3 gap-1">
              <FileUploadButton
                fileInputRef={fileInputRef}
                handleFileUpload={handleFileUpload}
                isEventConnected={isEventConnected}
                agentStatus={agentStatus}
                isSandboxReady={isSandboxReady}
                readOnly={readOnly}
              />
              
              <SendButton
                agentStatus={agentStatus}
                handleStopGeneration={handleStopGeneration}
                isEventConnected={isEventConnected}
                isSandboxReady={isSandboxReady}
                readOnly={readOnly}
                hasOversizedFiles={hasOversizedFiles}
              />
            </div>
        </div>
      </form>
    </div>
  );
});

InputArea.displayName = 'InputArea';

export default InputArea;